import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/components/admin/auth";
import { PageTitle, Panel } from "@/components/admin/AdminLayout";

const AddUser = () => {
  const qc = useQueryClient();
  const [f, setF] = useState({ email: "", name: "", role: "editor", password: "" });
  const save = async () => {
    try { await api.post("/admin/users", f); toast.success("User added"); setF({ email: "", name: "", role: "editor", password: "" }); qc.invalidateQueries({ queryKey: ["users"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const cls = "h-10 rounded-md border border-slate-200 px-3 text-sm";
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="add-user-form">
      <input className={cls} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" />
      <input className={cls} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" />
      <select className={cls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="new-user-role"><option value="editor">Editor</option><option value="admin">Admin</option></select>
      <input className={cls} type="password" placeholder="Temporary password (10+)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password" />
      <button onClick={save} className="bg-emerald-600 text-white font-bold px-4 rounded-lg" data-testid="add-user-button">Add user</button>
    </div>
  );
};

export default function Team() {
  const qc = useQueryClient();
  const { user } = useAuth();
  if (user.role !== "admin") return <Navigate to="/admin" replace />;
  return <TeamInner qc={qc} user={user} />;
}

function TeamInner({ qc, user }) {
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.get("/admin/users").then((r) => r.data) });
  const logs = useQuery({ queryKey: ["audit"], queryFn: () => api.get("/admin/audit").then((r) => r.data) });
  const msgs = useQuery({ queryKey: ["messages"], queryFn: () => api.get("/admin/messages").then((r) => r.data) });
  const del = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); qc.invalidateQueries({ queryKey: ["users"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-team-page">
      <PageTitle title="Team & Audit" sub="Admins control everything. Editors can manage deals, catalog and AI drafts, but cannot change settings, automations, users or import commission reports." />
      <Panel title="Users" testid="users-panel">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[560px]"><tbody>
          {(users.data || []).map((u) => (
            <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid={`user-row-${u.email}`}>
              <td className="p-2 font-semibold">{u.name}</td><td className="p-2">{u.email}</td><td className="p-2 uppercase text-xs font-bold text-amber-700">{u.role}</td>
              <td className="p-2 text-xs text-slate-500">Last login {fmtDateTime(u.last_login_at)}</td>
              <td className="p-2 text-right">{u.id !== user.id && <button onClick={() => del(u)} className="text-red-600 p-1" data-testid="delete-user-button"><Trash2 size={15} /></button>}</td>
            </tr>
          ))}
        </tbody></table></div>
        <AddUser />
      </Panel>
      <Panel title="Contact messages" testid="messages-panel">
        {(msgs.data || []).map((m) => <div key={m.id} className="text-sm py-2 border-b border-slate-100 last:border-0"><b>{m.name}</b> &lt;{m.email}&gt; · <span className="text-xs text-slate-500">{fmtDateTime(m.created_at)}</span><p className="text-slate-600">{m.message}</p></div>)}
        {msgs.data && !msgs.data.length && <p className="text-sm text-slate-500">No messages.</p>}
      </Panel>
      <Panel title="Audit log" testid="audit-log-panel">
        <div className="overflow-x-auto max-h-[480px]"><table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 text-left sticky top-0 bg-white"><tr><th className="p-2">When</th><th className="p-2">Who</th><th className="p-2">Action</th><th className="p-2">Entity</th><th className="p-2">Detail</th></tr></thead>
          <tbody>{(logs.data || []).map((l) => <tr key={l.id} className="border-t border-slate-100"><td className="p-2 whitespace-nowrap text-xs">{fmtDateTime(l.created_at)}</td><td className="p-2 text-xs">{l.user_email}</td><td className="p-2 font-semibold">{l.action}</td><td className="p-2">{l.entity}</td><td className="p-2 text-xs text-slate-500 max-w-xs truncate">{l.detail}</td></tr>)}</tbody>
        </table></div>
      </Panel>
    </div>
  );
}
